import { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import type { LucideIcon } from 'lucide-react';
import {
  ChevronRight,
  Code,
  KeyRound,
  LayoutDashboard,
  Lock,
  Logs,
  Cable,
  FileLock2,
  ShieldCheck,
  UserCog,
  Users,
  UsersRound,
} from 'lucide-react';
import { Outlet, useLocation, useMatches } from 'react-router-dom';

import Breadcrumbs from '@/components/ui/breadcrumbs';
import ColorModeToggle from '@/components/ui/color-mode-toggle';
import LeftNavItem from '@/components/ui/left-nav-item';
import LeftNavLayout from '@/components/ui/left-nav-layout';
import LeftNavSection from '@/components/ui/left-nav-section';
import { RouteSkeleton } from '@/components/ui/route-skeleton';
import Navbar from '@/components/ui/navbar';
import SidebarTenantPicker from '@/components/nav/sidebar-tenant-picker';
import SidebarUserProfile from '@/components/nav/sidebar-user-profile';
import { BreadcrumbLink } from '@/components/nav/breadcrumb-link';
import PageContentTransition from '@/components/page-content-transition';
import {
  useCurrentTenant,
  useHasPermission,
  useOptionalCurrentTenantSlug,
} from '@/hooks/scoped';
import {
  useBreadcrumbs,
  useFlexHeight,
  useHideBreadcrumbs,
} from '@/hooks/use-breadcrumbs';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ADMIN_AREA_ENABLED } from '@/config/feature-gates';
import { useStaffIdentity } from '@/hooks/admin';
import {
  pathToAdmin,
  pathToConnectors,
  pathToSecrets,
  pathToSensitiveContent,
  pathToEnrollment,
  pathToSettingsClients,
  pathToSettingsMembers,
  pathToSettingsRoles,
  pathToSettingsTeams,
  pathToTenant,
  pathToTenantLogs,
} from '@/navigation';
import { SENSITIVE_CONTENT_READ } from '@/types/core/sensitive-content';
import { getIconPixels } from '@/theme/size-tokens';
import { staffTierOf } from '@/types/control/staff';

import { adminSectionsForTier } from './admin/admin-sections';

type NavItemConfig = {
  icon: LucideIcon;
  label: string;
  href: string;
  matchType?: 'exact' | 'startsWith';
};

type NavSectionConfig = {
  label: string;
  items: NavItemConfig[];
};

type RouteHandle = {
  breadcrumb?: string;
  isTab?: boolean;
  tabParam?: string;
};

export default function Layout() {
  const location = useLocation();
  const matches = useMatches();

  const stableKey = useMemo(() => {
    const deepest = matches[matches.length - 1];
    const handle = deepest?.handle as RouteHandle | undefined;

    if (handle?.isTab) {
      const segments = location.pathname.split('/');
      return segments.slice(0, -1).join('/');
    }

    if (handle?.tabParam) {
      const params = deepest?.params as Record<string, string | undefined>;
      if (params?.[handle.tabParam]) {
        const segments = location.pathname.split('/');
        return segments.slice(0, -1).join('/');
      }
    }

    return location.pathname;
  }, [location.pathname, matches]);

  const tenantSlug = useOptionalCurrentTenantSlug();
  const { data: tenant } = useCurrentTenant();
  const { allowed: canReadSensitiveContent } = useHasPermission(
    tenant?.id,
    SENSITIVE_CONTENT_READ
  );
  const flexHeight = useFlexHeight();
  const breadcrumbItems = useBreadcrumbs();
  const hideBreadcrumbs = useHideBreadcrumbs();
  useDocumentTitle(tenant?.name);

  useEffect(() => {
    if (!tenantSlug) {
      return;
    }

    void import('./scoped/home');
  }, [tenantSlug]);

  const tenantHomeHref = useMemo(() => {
    if (!tenantSlug) {
      return '/';
    }

    return pathToTenant(tenantSlug);
  }, [tenantSlug]);

  // Gated so that the cross-origin staff-tier request, fired for every user on
  // every page load, can be switched off without unmounting the admin routes.
  const { data: staffIdentity } = useStaffIdentity({
    enabled: ADMIN_AREA_ENABLED,
  });
  const staffTier = staffTierOf(staffIdentity ?? null);

  // Cross-tenant, so it is appended whether or not a tenant is selected.
  const staffSections = useMemo<NavSectionConfig[]>(() => {
    if (!ADMIN_AREA_ENABLED || staffTier === 'none') {
      return [];
    }

    return [
      {
        label: 'Hydden Staff',
        items: [
          {
            icon: ShieldCheck,
            label: 'Administration',
            href: pathToAdmin(),
            matchType: 'exact',
          },
          ...adminSectionsForTier(staffTier).map((section) => ({
            icon: section.icon,
            label: section.label,
            href: section.href,
            matchType: 'startsWith' as const,
          })),
        ],
      },
    ];
  }, [staffTier]);

  const navSections = useMemo<NavSectionConfig[]>(() => {
    if (!tenantSlug) return staffSections;

    return [
      {
        label: 'Catalog',
        items: [
          {
            icon: LayoutDashboard,
            label: 'Dashboard',
            href: tenantHomeHref,
            matchType: 'exact',
          },
          {
            icon: Logs,
            label: 'Logs',
            href: pathToTenantLogs(tenantSlug),
            matchType: 'startsWith',
          },
        ],
      },
      // {
      //   label: 'Engine',
      //   items: [
      //     {
      //       icon: Bolt,
      //       label: 'Evaluators',
      //       href: pathToEvaluators(tenantSlug),
      //       matchType: 'startsWith',
      //     },
      //     {
      //       icon: Workflow,
      //       label: 'Automations',
      //       href: pathToAutomations(tenantSlug),
      //       matchType: 'startsWith',
      //     },
      //     {
      //       icon: Activity,
      //       label: 'Events',
      //       href: pathToEngineEvents(tenantSlug),
      //       matchType: 'startsWith',
      //     },
      //     {
      //       icon: Server,
      //       label: 'Runners',
      //       href: pathToEngineRunners(tenantSlug),
      //       matchType: 'startsWith',
      //     },
      //   ],
      // },
      {
        label: 'Integrations',
        items: [
          {
            icon: Cable,
            label: 'Connectors',
            href: pathToConnectors(tenantSlug),
            matchType: 'startsWith',
          },
          {
            icon: Lock,
            label: 'Secrets',
            href: pathToSecrets(tenantSlug),
            matchType: 'startsWith',
          },
          ...(canReadSensitiveContent
            ? [
                {
                  icon: FileLock2,
                  label: 'Sensitive Content',
                  href: pathToSensitiveContent(tenantSlug),
                  matchType: 'startsWith' as const,
                },
              ]
            : []),
        ],
      },
      {
        label: 'Org Settings',
        items: [
          {
            icon: Users,
            label: 'Members',
            href: pathToSettingsMembers(tenantSlug),
            matchType: 'startsWith',
          },
          {
            icon: UserCog,
            label: 'Roles',
            href: pathToSettingsRoles(tenantSlug),
            matchType: 'startsWith',
          },
          {
            icon: UsersRound,
            label: 'Teams',
            href: pathToSettingsTeams(tenantSlug),
            matchType: 'startsWith',
          },
          {
            icon: Code,
            label: 'Clients',
            href: pathToSettingsClients(tenantSlug),
            matchType: 'startsWith',
          },
          {
            icon: KeyRound,
            label: 'Enrollment',
            href: pathToEnrollment(tenantSlug),
            matchType: 'startsWith',
          },
        ],
      },
      ...staffSections,
    ];
  }, [canReadSensitiveContent, staffSections, tenantHomeHref, tenantSlug]);

  const isNavItemActive = (
    href: string,
    matchType: NavItemConfig['matchType'] = 'startsWith'
  ) => {
    return matchType === 'exact'
      ? location.pathname === href
      : location.pathname.startsWith(href);
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const sideContentRef = useRef<HTMLDivElement | null>(null);

  const handleSidebarCollapsedChange = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    try {
      localStorage.setItem('sidebar-collapsed', String(collapsed));
    } catch {
      // ignore
    }
  };

  const navbarActions = (
    <>
      <ColorModeToggle className="px-3!" />
    </>
  );

  const pageContent = (
    <div
      ref={sideContentRef}
      data-scroll-root="true"
      className={flexHeight ? 'flex h-full flex-col' : undefined}
    >
      <Suspense fallback={<RouteSkeleton />}>
        <Outlet />
      </Suspense>
    </div>
  );

  const navbarBrand = null;

  const navbar = (
    <Navbar
      brand={navbarBrand}
      actions={navbarActions}
      border="bottom"
      sticky={false}
    >
      {!hideBreadcrumbs && breadcrumbItems.length > 0 && (
        <Breadcrumbs
          linkComponent={BreadcrumbLink}
          items={breadcrumbItems}
          separator={<ChevronRight size={getIconPixels('xs')} />}
        />
      )}
    </Navbar>
  );

  return (
    <LeftNavLayout
      collapsed={sidebarCollapsed}
      header={'Navigation'}
      homeHref={tenantHomeHref}
      navWidth="14rem"
      navWidthCollapsed="3.75rem"
      topNav={navbar}
      onToggleCollapsed={() => handleSidebarCollapsedChange(!sidebarCollapsed)}
      footer={(collapsed) => (
        <>
          <SidebarTenantPicker collapsed={collapsed} />
          <SidebarUserProfile collapsed={collapsed} />
        </>
      )}
      nav={
        <>
          {navSections.map((section) => (
            <LeftNavSection
              key={section.label}
              label={section.label}
              collapsible
            >
              {section.items.map((item) => (
                <LeftNavItem
                  key={item.href}
                  icon={item.icon}
                  iconSize="sm"
                  href={item.href}
                  active={isNavItemActive(item.href, item.matchType)}
                >
                  {item.label}
                </LeftNavItem>
              ))}
            </LeftNavSection>
          ))}
        </>
      }
    >
      <PageContentTransition
        key={stableKey}
        transitionKey={stableKey}
        className={flexHeight ? 'flex h-full min-h-0 flex-col' : undefined}
      >
        {pageContent}
      </PageContentTransition>
    </LeftNavLayout>
  );
}
